import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Loading from '../components/Loading';
import { useGetRouletteCompanyReportQuery } from '../services/roulette/rouletteApi';

const today = new Date().toISOString().split('T')[0];

const THEME = '#7c3aed';

const RouletteCompanyReportPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  let { userId: stateUserId, fromDate: initFrom, toDate: initTo } = location.state || {};

  let userId = stateUserId;
  if (!userId) {
    try {
      const stored = localStorage.getItem('user_data');
      if (stored) userId = JSON.parse(stored).userId || '';
    } catch {}
  }

  const [fromDate, setFromDate] = useState(initFrom || today);
  const [toDate, setToDate]     = useState(initTo   || today);
  const [queryParams, setQueryParams] = useState({ userId, fromDate: initFrom || today, toDate: initTo || today });

  useEffect(() => {
    const f = initFrom || today;
    const t = initTo   || today;
    setFromDate(f);
    setToDate(t);
    setQueryParams({ userId, fromDate: f, toDate: t });
  }, [location.state]);

  const handleApply = () => setQueryParams({ userId, fromDate, toDate });

  const { data: reportData, isLoading, isFetching, error } = useGetRouletteCompanyReportQuery(
    queryParams,
    { skip: !queryParams.userId }
  );

  const rows   = reportData?.data || [];
  const totals = reportData?.summary || reportData?.totals || {};
  const viewUser     = reportData?.viewUser || {};
  const canDrilldown = !viewUser.userType || viewUser.userType !== 'Client';

  const handleUsernameClick = (clickedUserId) => {
    navigate('/roulette/company-report', {
      state: { userId: clickedUserId, fromDate: queryParams.fromDate, toDate: queryParams.toDate }
    });
  };

  const colorCls = (v) => Number(v) >= 0 ? 'text-green-600' : 'text-red-500';
  const fmt      = (v) => Number(v || 0).toFixed(2);

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full px-2">
          <div className="bg-white rounded shadow border border-gray-200">

            {/* Header */}
            <div className="px-5 py-3 flex items-center justify-between" style={{ background: THEME }}>
              <span className="text-xl text-white font-normal uppercase">Roulette P/L Report</span>
              <button onClick={() => navigate(-1)} className="bg-white text-gray-700 px-4 py-1.5 hover:bg-gray-100 transition-colors">
                Back
              </button>
            </div>

            {/* Date Filter */}
            <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">From</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={e => setFromDate(e.target.value)}
                  className="border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">To</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={e => setToDate(e.target.value)}
                  className="border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500"
                />
              </div>
              <button
                onClick={handleApply}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 text-sm transition-colors"
              >
                Apply
              </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {isLoading || isFetching ? (
                <div className="flex justify-center py-8"><Loading /></div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center">{error?.message || 'Error loading data'}</div>
              ) : (
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr style={{ background: THEME }} className="text-white">
                      <th className="py-3 px-4 text-left font-medium border-r border-purple-500">Username</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-purple-500">Roulette Amt</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-purple-500">Total</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-purple-500">Commission</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-purple-500">Total Amount</th>
                      <th className="py-3 px-4 text-left font-medium border-r border-purple-500">My Share</th>
                      <th className="py-3 px-4 text-left font-medium">Net Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-gray-500">No data available</td>
                      </tr>
                    ) : (
                      rows.map((row, index) => (
                        <tr key={index} className="hover:bg-gray-50 border-t border-gray-200">
                          <td className="py-3 px-4 text-gray-800 border-r border-gray-200">
                            {canDrilldown && row.userId && row.userType !== 'Client' && row.userType !== 'client' ? (
                              <button
                                onClick={() => handleUsernameClick(row.userId)}
                                className="text-blue-600 hover:underline text-left"
                              >
                                <div className="font-semibold">{row.userId || row.username || '-'}</div>
                                {row.name && <div className="text-xs text-gray-500 font-normal">{row.name}</div>}
                              </button>
                            ) : (
                              <span>
                                <div className="font-semibold">{row.userId || row.username || '-'}</div>
                                {row.name && <div className="text-xs text-gray-500">{row.name}</div>}
                              </span>
                            )}
                          </td>
                          <td className={`py-3 px-4 border-r border-gray-200 ${colorCls(row.rouletteAmt ?? row.casinoAmt)}`}>
                            {fmt(row.rouletteAmt ?? row.casinoAmt)}
                          </td>
                          <td className={`py-3 px-4 border-r border-gray-200 ${colorCls(row.totalAmount ?? row.total)}`}>
                            {fmt(row.totalAmount ?? row.total)}
                          </td>
                          <td className={`py-3 px-4 border-r border-gray-200 ${colorCls(row.rouletteComm ?? row.commission)}`}>
                            {fmt(row.rouletteComm ?? row.commission)}
                          </td>
                          <td className={`py-3 px-4 border-r border-gray-200 ${colorCls(row.totalAmount)}`}>
                            {fmt(row.totalAmount)}
                          </td>
                          <td className={`py-3 px-4 border-r border-gray-200 ${colorCls(row.myShare)}`}>
                            {fmt(row.myShare)}
                          </td>
                          <td className={`py-3 px-4 ${colorCls(row.netAmount)}`}>
                            {fmt(row.netAmount)}
                          </td>
                        </tr>
                      ))
                    )}
                    {rows.length > 0 && (
                      <tr className="bg-yellow-50 border-t-2 border-yellow-600 font-bold">
                        <td className="py-3 px-4 border-r border-gray-200">Total</td>
                        <td className={`py-3 px-4 border-r border-gray-200 ${colorCls(totals.rouletteAmt ?? totals.casinoAmt)}`}>
                          {fmt(totals.rouletteAmt ?? totals.casinoAmt)}
                        </td>
                        <td className={`py-3 px-4 border-r border-gray-200 ${colorCls(totals.totalAmount ?? totals.total)}`}>
                          {fmt(totals.totalAmount ?? totals.total)}
                        </td>
                        <td className={`py-3 px-4 border-r border-gray-200 ${colorCls(totals.rouletteComm ?? totals.commission)}`}>
                          {fmt(totals.rouletteComm ?? totals.commission)}
                        </td>
                        <td className={`py-3 px-4 border-r border-gray-200 ${colorCls(totals.totalAmount)}`}>
                          {fmt(totals.totalAmount)}
                        </td>
                        <td className={`py-3 px-4 border-r border-gray-200 ${colorCls(totals.myShare)}`}>
                          {fmt(totals.myShare)}
                        </td>
                        <td className={`py-3 px-4 ${colorCls(totals.netAmount)}`}>
                          {fmt(totals.netAmount)}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default RouletteCompanyReportPage;
